import { Box, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import EducationShell from "./EducationShell";

// ─── Palette — matches EducationLanding.jsx / TypesLanding.jsx ───────────────
const INK = "#0F172A";
const SLATE = "#475569";
const MUTED = "#64748B";
const BORDER = "#E2E8F0";
const ACCENT = "#0F766E";
const ACCENT_SOFT = "#F0FDFA";
const PAPER = "#FFFFFF";
const CANVAS = "#F8FAFC";

/**
 * Shared layout for the 6 CKD complication deep-dive pages (Cardiovascular,
 * Neurological, Gastrointestinal, Bone & Mineral, Hematologic/Anemia, and
 * Fluid & Electrolyte). Each page supplies its own content via props; this
 * component only owns structure and styling.
 *
 * @param {string} code            e.g. "CV"
 * @param {string} name            e.g. "Cardiovascular Complications"
 * @param {string} subtitle        e.g. "The leading cause of death in CKD patients"
 * @param {string} summary         1–2 sentence overview shown in the header
 * @param {string} whatItIs        "What it is" section body
 * @param {Array<{label: string, note: string}>} keyFeatures
 * @param {string} monitoring      "How it's monitored" section body
 * @param {string} management      "Management" section body
 * @param {Array<{label: string, url: string}>} relatedLinks
 */
export default function ComplicationPageTemplate({
  code,
  name,
  subtitle,
  summary,
  whatItIs,
  keyFeatures = [],
  monitoring,
  management,
  relatedLinks = [],
}) {
  const navigate = useNavigate();

  return (
    <EducationShell
      breadcrumbs={[
        { label: "Learn", path: "/education" },
        { label: "CKD Complications", path: "/education/basics" },
        { label: code },
      ]}
    >
      <Box sx={{ fontFamily: "'DM Sans', sans-serif", bgcolor: PAPER }}>
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=Fraunces:ital,wght@0,300;0,600;1,300&display=swap');
        `}</style>

        {/* ── Header ── */}
        <Box sx={{ px: { xs: 3, md: 8 }, pt: { xs: 5, md: 7 }, pb: { xs: 4, md: 5 }, borderBottom: `1px solid ${BORDER}` }}>
          <Box
            sx={{
              display: "inline-flex",
              px: 1.5,
              py: 0.5,
              borderRadius: "6px",
              bgcolor: ACCENT_SOFT,
              color: ACCENT,
              fontSize: "0.75rem",
              fontWeight: 700,
              letterSpacing: "0.04em",
              mb: 2,
            }}
          >
            {code}
          </Box>
          <Typography sx={{ fontFamily: "'Fraunces', serif", fontSize: { xs: "1.8rem", md: "2.4rem" }, fontWeight: 600, color: INK, mb: 0.5 }}>
            {name}
          </Typography>
          <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: MUTED, mb: 2.5 }}>
            {subtitle}
          </Typography>
          {summary && (
            <Typography sx={{ fontSize: "1rem", color: SLATE, maxWidth: 680, lineHeight: 1.75 }}>
              {summary}
            </Typography>
          )}
        </Box>

        {/* ── What it is ── */}
        {whatItIs && (
          <Box sx={{ px: { xs: 3, md: 8 }, py: { xs: 5, md: 6 } }}>
            <SectionLabel>What it is</SectionLabel>
            <Typography sx={{ fontSize: "0.95rem", color: SLATE, lineHeight: 1.8, maxWidth: 720 }}>
              {whatItIs}
            </Typography>
          </Box>
        )}

        {/* ── Key features ── */}
        {keyFeatures.length > 0 && (
          <Box sx={{ px: { xs: 3, md: 8 }, py: { xs: 5, md: 6 }, bgcolor: CANVAS }}>
            <SectionLabel>What to watch for</SectionLabel>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2, mt: 2 }}>
              {keyFeatures.map((f) => (
                <Box key={f.label} sx={{ border: `1px solid ${BORDER}`, borderRadius: "8px", p: 2.5, bgcolor: PAPER }}>
                  <Typography sx={{ fontSize: "0.9rem", fontWeight: 700, color: INK, mb: 0.5 }}>
                    {f.label}
                  </Typography>
                  <Typography sx={{ fontSize: "0.85rem", color: SLATE, lineHeight: 1.6 }}>
                    {f.note}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* ── Monitoring ── */}
        {monitoring && (
          <Box sx={{ px: { xs: 3, md: 8 }, py: { xs: 5, md: 6 } }}>
            <SectionLabel>How it's monitored</SectionLabel>
            <Typography sx={{ fontSize: "0.95rem", color: SLATE, lineHeight: 1.8, maxWidth: 720 }}>
              {monitoring}
            </Typography>
          </Box>
        )}

        {/* ── Management ── */}
        {management && (
          <Box sx={{ px: { xs: 3, md: 8 }, py: { xs: 5, md: 6 }, bgcolor: CANVAS }}>
            <SectionLabel>Management</SectionLabel>
            <Typography sx={{ fontSize: "0.95rem", color: SLATE, lineHeight: 1.8, maxWidth: 720 }}>
              {management}
            </Typography>
          </Box>
        )}

        {/* ── Related trials / links ── */}
        {relatedLinks.length > 0 && (
          <Box sx={{ px: { xs: 3, md: 8 }, py: { xs: 5, md: 6 } }}>
            <SectionLabel>Related trials &amp; tools</SectionLabel>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mt: 2, maxWidth: 720 }}>
              {relatedLinks.map((link) => (
                <Box
                  key={link.label}
                  onClick={() => (link.external ? window.open(link.url, "_blank", "noopener,noreferrer") : navigate(link.url))}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    border: `1px solid ${BORDER}`,
                    borderRadius: "8px",
                    px: 2.5,
                    py: 1.75,
                    cursor: "pointer",
                    "&:hover": { borderColor: ACCENT },
                  }}
                >
                  <Typography sx={{ fontSize: "0.9rem", fontWeight: 600, color: INK }}>
                    {link.label}
                  </Typography>
                  <ArrowForwardIcon sx={{ fontSize: 16, color: MUTED }} />
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </EducationShell>
  );
}

function SectionLabel({ children }) {
  return (
    <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase", color: ACCENT, mb: 2 }}>
      {children}
    </Typography>
  );
}
